<template>
  <div class="chart-container">
    <div class="chart-header">
      <span class="chart-title">{{ title }}</span>
      <CustomMultiSelect
        v-model="selectedModes"
        :options="dataOptions"
        placeholder="Filtro"
        :filter="false"
        display="chip"
        @change="handleFilterChange"
      />
    </div>
    <p class="chart-subtitle" style="margin-top: 20px" v-if="text">{{ text }}</p>
    <p class="chart-text" style="text-align: left" v-if="subtext">{{ subtext }}</p>

    <div class="chart-legend" v-if="legends">
      <CustomLegend
        v-for="legend in legends"
        :key="legend.text"
        :color="legend.color"
        :text="legend.text"
      />
    </div>
    <canvas :id="chartId" class="ocupancy-chart"></canvas>
  </div>
</template>

<script>
import CustomMultiSelect from '@/Shared/CustomMultiSelect.vue'
import CustomLegend from '@/Shared/Charts/CustomLegend.vue'
import { Chart, registerables } from 'chart.js'
import moment from 'moment'

Chart.register(...registerables)

export default {
  name: 'HotelOccupancyChart',
  components: {
    CustomMultiSelect,
    CustomLegend,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    text: {
      type: String,
      required: false,
    },
    subtext: {
      type: String,
      required: false,
    },
    hasFilter: Boolean,
    chartType: {
      type: String,
      default: 'bar',
    },
    dataOptions: {
      type: Array,
      required: true,
    },
    legends: {
      type: Array,
      required: false,
    },
    dataSets: {
      type: Array,
      required: true,
    },
    chartId: {
      type: String,
      required: true,
    },
    indexAxis: {
      type: String,
      required: false,
      default: 'x',
    },
    selectedModes: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      chart: null,
    }
  },
  mounted() {
    this.createChart()
  },
  methods: {
    handleFilterChange(selectedModes) {
      this.$emit('filter-change', selectedModes)
      this.createChart(selectedModes)
    },
    createChart(selectedModes = null) {
      if (this.chart) {
        this.chart.destroy()
      }

      let datasets = []
      let labels = []

      if (!selectedModes || selectedModes.length === 0) {
        this.dataSets.forEach((dataSet) => {
          const data = this.processData(dataSet.answers)
          labels = [...new Set([...labels, ...data.map((d) => d.date)])].sort()
        })
        this.dataSets.forEach((dataSet) => {
          const data = this.processData(dataSet.answers)
          const dataset = this.buildDataset(data, labels, dataSet.label, dataSet.color)
          datasets.push(dataset)
        })
      } else {
        selectedModes.forEach((selectedMode) => {
          const dataSet = this.dataSets.find((ds) => ds.label === selectedMode.label)
          if (dataSet) {
            const data = this.processData(dataSet.answers)
            labels = [...new Set([...labels, ...data.map((d) => d.date)])].sort()
          }
        })
        selectedModes.forEach((selectedMode) => {
          const dataSet = this.dataSets.find((ds) => ds.label === selectedMode.label)
          if (dataSet) {
            const data = this.processData(dataSet.answers)
            const dataset = this.buildDataset(data, labels, dataSet.label, dataSet.color)
            datasets.push(dataset)
          }
        })
      }

      const ctx = document.getElementById(this.chartId).getContext('2d')
      this.chart = new Chart(ctx, {
        type: this.chartType,
        data: {
          labels,
          datasets,
        },
        options: {
          indexAxis: this.indexAxis,
          responsive: true,
          maintainAspectRatio: true,
          spanGaps: true,
          elements: {
            line: {
              tension: 0.4,
            },
            point: {
              radius: 3,
              hoverRadius: 6,
              hitRadius: 10,
            },
          },
          plugins: {
            legend: { display: false },
            title: { display: true },
            tooltip: {
              enabled: true,
              mode: 'nearest',
              intersect: true,
              callbacks: {
                label: function (context) {
                  return `${context.dataset.data[context.dataIndex]}%`
                },
              },
            },
          },
          interaction: {
            intersect: false,
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
              },
            },
            y: {
              display: true,
              title: {
                display: false,
              },
              ticks: {
                callback: function (value) {
                  return value + '%'
                },
              },
            },
          },
        },
      })
    },
    processData(answers) {
      if (!answers) {
        return []
      }
      const groupedData = answers.map((answer) => ({
        date: moment(answer.answer_date).format('MMMM YYYY'),
        value: answer.percent,
      }))

      const dateMap = {}
      groupedData.forEach((data) => {
        if (!dateMap[data.date]) {
          dateMap[data.date] = 0
        }
        dateMap[data.date] = data.value
      })

      const processedData = []
      for (const [date, value] of Object.entries(dateMap)) {
        processedData.push({ date, value })
      }

      return processedData
    },
    buildDataset(data, labels, label, borderColor) {
      const dataset = labels.map((label) => {
        const found = data.find((d) => d.date === label)
        return found ? found.value : 0
      })

      return {
        label,
        data: dataset,
        borderColor,
        backgroundColor: borderColor,
        fill: false,
      }
    },
  },

  watch: {
    selectedModes() {
      this.createChart(this.selectedModes)
    },
  },
}
</script>

<style src="./styles.css">
</style>
